import { Columns3, LayoutDashboard, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { apiClient } from '../../lib/api';
import { CLIENT_NAME } from '../../lib/branding';
import { Wordmark } from '../brand/Wordmark';

type Me = {
  id: string;
  email: string;
  display_name: string;
  role: 'admin' | 'rm' | 'manager' | string;
  preferred_locale: string;
};

type NavItem = {
  to: string;
  label: string;
  sublabel: string;
  /** Compact label used by the mobile bottom bar where horizontal space
   *  is tight. Falls back to `label` if absent. */
  shortLabel?: string;
  /** Lucide icon component rendered in the mobile bottom bar. Typed as
   *  `typeof Users` so the TSX consumer can read it as a component. */
  icon: typeof Users;
  managerOnly?: boolean;
};

const NAV: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    shortLabel: 'Accueil',
    sublabel: '01',
    icon: LayoutDashboard,
  },
  {
    to: '/contacts',
    label: 'Leads / Contacts',
    shortLabel: 'Contacts',
    sublabel: '02',
    icon: Users,
  },
  { to: '/pipeline', label: 'Pipeline', shortLabel: 'Pipeline', sublabel: '03', icon: Columns3 },
  {
    to: '/leads',
    label: 'Opportunities',
    shortLabel: 'Affaires',
    sublabel: '04',
    icon: TrendingUp,
  },
  {
    to: '/manager/opportunities',
    label: 'Manager view',
    shortLabel: 'Manager',
    sublabel: 'M',
    icon: ShieldCheck,
    managerOnly: true,
  },
];

export function AppShell() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient
      .me()
      .then((u) => setMe(u as Me))
      .catch(() => {
        navigate('/', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  async function logout() {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
    navigate('/', { replace: true });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-glacier flex items-center justify-center text-ink-tertiary text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-glacier">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-graphite text-white">
        <div className="px-6 py-8">
          <Wordmark size="md" tone="on-dark" />
          <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-white/50">
            {CLIENT_NAME} · Internal MVP
          </p>
        </div>
        <nav className="mt-4 flex-1 px-3 space-y-1">
          {NAV.filter(
            (item) => !item.managerOnly || me?.role === 'manager' || me?.role === 'admin',
          ).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                'flex items-baseline gap-3 px-3 py-2.5 rounded-card text-sm transition-colors ' +
                (isActive
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:bg-white/5 hover:text-white')
              }
            >
              <span className="font-display text-base text-white/40 w-5">{item.sublabel}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-5 pb-6 mt-auto">
          <p className="label-uppercase !text-white/40">— Signed in</p>
          {me && (
            <>
              <p className="mt-2 text-sm leading-tight">{me.display_name}</p>
              <p className="text-xs text-white/50 mt-0.5 uppercase tracking-wide">{me.role}</p>
            </>
          )}
          <button
            type="button"
            onClick={logout}
            className="mt-4 w-full text-left text-xs uppercase tracking-[0.16em] text-white/60 hover:text-white transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile / tablet top bar replaces sidebar on < lg */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-graphite text-white px-4 py-3 flex items-center justify-between">
        <Wordmark size="sm" tone="on-dark" />
        {me && (
          <p className="text-xs text-white/70">
            {me.display_name} · <span className="uppercase">{me.role}</span>
          </p>
        )}
      </header>

      {/* Mobile / tablet bottom nav. Fixed at the bottom so the thumb
          reaches it on phones, and so a horizontal label overflow is
          impossible by construction — icons + short labels stacked
          vertically inside flex-1 cells. */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-graphite border-t border-white/10 flex pb-[env(safe-area-inset-bottom)]"
        aria-label="Navigation principale"
      >
        {NAV.filter(
          (item) => !item.managerOnly || me?.role === 'manager' || me?.role === 'admin',
        ).map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                'flex-1 min-h-touch py-2 flex flex-col items-center justify-center gap-1 text-[10px] transition-colors ' +
                (isActive ? 'text-white' : 'text-white/50 hover:text-white/80')
              }
            >
              <Icon aria-hidden="true" strokeWidth={2} className="w-6 h-6" />
              <span>{item.shortLabel ?? item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Content scrolls under the fixed chrome. On < lg we reserve 52px
          on top (the top bar) and ~72px + iOS safe-area at the bottom
          so the last element isn't masked by the bottom nav. */}
      <main className="flex-1 min-w-0 lg:pl-0 pt-[52px] lg:pt-0 pb-[calc(72px+env(safe-area-inset-bottom))] lg:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
